import { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Workspace, BrandAsset, ClassifiedImage, TemplateSummary, Template } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { renderCompositeThumbnail } from '../utils/canvasRenderer';
import { Button } from './Button';
import { Input, InputWrapper } from './Input';
import { Modal, ModalActions } from './Modal';

// ─── Types ───────────────────────────────────────────────────────────────────

type WizardStep =
  | 'source-choice'
  | 'greeting'
  | 'scanning'
  | 'brand-name'
  | 'logo-select'
  | 'photo-choice'
  | 'photo-select'
  | 'photo-upload'
  | 'template-select'
  | 'content-mix'
  | 'creating'
  | 'done'
  | 'logo-alt'
  | 'manual-name'
  | 'manual-logo'
  | 'url-input'
  | 'document-upload'
  | 'document-analyzing'
  | 'font-preview'
  | 'brand-review';

interface ContentMix {
  education: number;
  connection: number;
  proof: number;
  promotion: number;
}

interface ScanResult {
  url: string;
  suggestedName: string;
  images: ClassifiedImage[];
  brandColors: string[];
  brandFonts: string[];
  brandInfo: Record<string, unknown>;
}

interface ChatMsg {
  id: number;
  from: 'novah' | 'user';
  text: string;
  widget?: 'url-input' | 'images' | 'templates' | 'sliders' | 'name-input' | 'logo-upload' | 'success';
  images?: ClassifiedImage[];
  selectMode?: 'single' | 'multi';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (workspace: Workspace) => void;
  inline?: boolean;
}

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

// ─── Styled Components ───────────────────────────────────────────────────────

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 55vh;
  overflow-y: auto;
  padding: 4px 2px;
  scroll-behavior: smooth;
  max-width: 100%;
  min-width: 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 2px; }
`;

const MsgRow = styled.div<{ $from: 'novah' | 'user' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  justify-content: ${({ $from }) => $from === 'user' ? 'flex-end' : 'flex-start'};
  animation: ${fadeIn} 0.3s ease-out;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.25);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Bubble = styled.div<{ $from: 'novah' | 'user' }>`
  max-width: 85%;
  padding: 8px 12px;
  border-radius: ${({ $from }) => $from === 'novah' ? '4px 12px 12px 12px' : '12px 4px 12px 12px'};
  font-size: 13px;
  line-height: 1.5;
  background: ${({ $from, theme }) => $from === 'novah' ? theme.colors.background : theme.colors.primary};
  color: ${({ $from, theme }) => $from === 'novah' ? theme.colors.textSecondary : '#fff'};
  border: ${({ $from, theme }) => $from === 'novah' ? theme.borders.thin : 'none'};
  strong { color: ${({ $from, theme }) => $from === 'novah' ? theme.colors.text : '#fff'}; }
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 10px 14px;
  span {
    width: 6px; height: 6px; border-radius: 50%;
    background: ${({ theme }) => theme.colors.textMuted};
    animation: ${dotBounce} 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

const InputArea = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 12px;
  margin-top: 8px;
  max-width: 100%;
  overflow: hidden;
  min-width: 0;
`;

const SourceChoiceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SourceChoiceBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: rgba(59, 130, 246, 0.04);
  }
`;

const SourceIcon = styled.span`
  font-size: 18px;
  width: 24px;
  text-align: center;
`;

const SourceLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const SourceDesc = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const UrlRow = styled.div`
  display: flex;
  gap: 8px;
`;

const UrlInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
`;

const ImgGrid = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  margin: 8px 0;
  max-width: 100%;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }
`;

const ImgItem = styled.div<{ $selected?: boolean }>`
  position: relative;
  flex: 0 0 80px;
  width: 80px;
  height: 80px;
  border: 3px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.15s ease;
  box-shadow: ${({ $selected }) => $selected ? '0 0 0 2px rgba(59,130,246,0.3)' : 'none'};
  scroll-snap-align: start;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.05);
  }
  img { width: 100%; height: 100%; object-fit: contain; padding: 4px; }
`;

const Badge = styled.div`
  position: absolute; top: 2px; right: 2px;
  width: 16px; height: 16px; border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff; font-size: 9px;
  display: flex; align-items: center; justify-content: center;
`;

const TplGrid = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 8px 2px;
  margin: 8px 0;
  max-width: 100%;
  scroll-snap-type: x mandatory;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 2px; }
`;

const TplCard = styled.div<{ $selected?: boolean }>`
  min-width: 130px;
  max-width: 130px;
  flex-shrink: 0;
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  transition: all 0.15s ease;
  opacity: ${({ $selected }) => $selected ? 1 : 0.5};
  scroll-snap-align: start;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; opacity: 1; }
  position: relative;
`;

const TplPreview = styled.div`
  aspect-ratio: 9/16;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; background: #f3f4f6;
  canvas { width: 100%; height: 100%; object-fit: contain; }
`;

const TplName = styled.div`
  padding: 5px 6px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TplActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 11px;
`;

const SelectAllLink = styled.button`
  background: none; border: none; padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px; font-weight: 600; cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const MixCheckboxGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 8px 0;
`;

const MixCheckItem = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid ${({ $checked, theme }) => $checked ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $checked }) => $checked ? 'rgba(59,130,246,0.04)' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const MixCheckbox = styled.input`
  accent-color: ${({ theme }) => theme.colors.primary};
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const MixLabel = styled.span`
  font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.text};
`;

const MixHint = styled.span`
  font-size: 11px; color: ${({ theme }) => theme.colors.textMuted};
`;

const MixSummary = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-top: 6px;
  padding: 6px;
  background: rgba(16,185,129,0.06);
  border-radius: 6px;
`;

const HiddenInput = styled.input`display: none;`;

const FontPreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0;
`;

const FontPreviewItem = styled.div<{ $selected?: boolean }>`
  padding: 12px;
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  background: ${({ $selected }) => $selected ? 'rgba(59,130,246,0.06)' : 'transparent'};
  transition: all 0.15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const FontName = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const FontSample = styled.div`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.3;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const SuccessBox = styled.div`
  text-align: center;
  padding: 12px 0;
  animation: ${fadeIn} 0.3s ease-out;
`;

// Sample backgrounds for template previews (stock-like images via Pexels)
const PREVIEW_SAMPLE_IMAGES = [
  'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400',
  'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=400',
  'https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg?auto=compress&cs=tinysrgb&w=400',
  'https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=400',
];

const SAMPLE_DYNAMIC_TEXTS: Record<string, string> = {
  headline: 'Your Brand',
  tagline: 'Tagline goes here',
  cta: 'Learn More',
  quote: 'Quality content that connects',
};

// ─── Template Thumbnail ──────────────────────────────────────────────────────

function TemplateThumbnail({ templateId, selected, onClick }: {
  templateId: string;
  selected: boolean;
  onClick: () => void;
}) {
  const [name, setName] = useState('');
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tpl = await apiClient.get<Template>(`/api/workspaces/__global__/templates/${templateId}`);
        if (cancelled) return;
        setName(tpl.name);
        const hash = templateId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
        const bg = PREVIEW_SAMPLE_IMAGES[hash % PREVIEW_SAMPLE_IMAGES.length];

        // Build dynamic texts map from template elements
        const dynamicTexts: Record<string, string> = {};
        for (const el of tpl.elements) {
          if (el.elementType === 'text' && (el as { isDynamic?: boolean }).isDynamic) {
            const dt = (el as { dynamicType?: string }).dynamicType || 'headline';
            if (SAMPLE_DYNAMIC_TEXTS[dt]) dynamicTexts[el.id] = SAMPLE_DYNAMIC_TEXTS[dt];
          }
        }

        const url = await renderCompositeThumbnail(bg, tpl, 200, { dynamicTexts });
        if (cancelled) return;
        setThumbUrl(url);
        setLoaded(true);
      } catch {
        setName('Template');
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  return (
    <TplCard $selected={selected} onClick={onClick}>
      <TplPreview>
        {thumbUrl ? (
          <img src={thumbUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 10, color: '#999' }}>{loaded ? name : 'Loading...'}</span>
        )}
      </TplPreview>
      <TplName>{name}</TplName>
      {selected && <Badge>✓</Badge>}
    </TplCard>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CreateWorkspaceModal({ isOpen, onClose, onCreated, inline }: Props) {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [step, setStep] = useState<WizardStep>('greeting');
  const [stepHistory, setStepHistory] = useState<WizardStep[]>([]);
  const [typing, setTyping] = useState(false);
  const [url, setUrl] = useState('');
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const scanResultRef = useRef<ScanResult | null>(null);
  const [brandName, setBrandName] = useState('');
  const [selectedLogo, setSelectedLogo] = useState<string | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [globalTemplates, setGlobalTemplates] = useState<TemplateSummary[]>([]);
  const [contentMix, setContentMix] = useState<ContentMix>({ education: 25, connection: 25, proof: 25, promotion: 25 });
  const [checkedCategories, setCheckedCategories] = useState<Set<keyof ContentMix>>(new Set(['education', 'connection', 'proof', 'promotion']));

  const toggleCategory = (cat: keyof ContentMix) => {
    setCheckedCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      if (next.size === 0) return prev; // prevent unchecking all
      // Auto-balance
      const cats = Array.from(next);
      const base = Math.floor(100 / cats.length);
      const remainder = 100 - base * cats.length;
      const newMix: ContentMix = { education: 0, connection: 0, proof: 0, promotion: 0 };
      cats.forEach((c, i) => { newMix[c] = base + (i < remainder ? 1 : 0); });
      setContentMix(newMix);
      return next;
    });
  };
  const [isCreating, setIsCreating] = useState(false);
  const [manualName, setManualName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [docFile, setDocFile] = useState<File | null>(null);
  const docFileRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [selectedFont, setSelectedFont] = useState<string | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);

  // Brand review editable fields
  const [reviewDescription, setReviewDescription] = useState('');
  const [reviewIndustry, setReviewIndustry] = useState('');
  const [reviewPhone, setReviewPhone] = useState('');
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewWebsite, setReviewWebsite] = useState('');
  const [reviewColors, setReviewColors] = useState<string[]>([]);
  const [colorInput, setColorInput] = useState('#3B82F6');
  const [photoUrlInput, setPhotoUrlInput] = useState('');
  const photoFileRef = useRef<HTMLInputElement>(null);
  const [createdWs, setCreatedWs] = useState<Workspace | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const msgIdRef = useRef(0);
  const analyzeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const nextId = () => ++msgIdRef.current;

  const pushStep = (next: WizardStep) => {
    setStepHistory(prev => [...prev, step]);
    setStep(next);
  };

  const goBack = () => {
    if (stepHistory.length === 0) return;
    const prev = stepHistory[stepHistory.length - 1];
    setStepHistory(h => h.slice(0, -1));
    setStep(prev);
  };

  const scroll = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  const addMsg = useCallback((msg: Omit<ChatMsg, 'id'>) => {
    setMessages(prev => [...prev, { ...msg, id: nextId() }]);
    scroll();
  }, [scroll]);

  const novah = useCallback((text: string, extra?: Partial<ChatMsg>) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      addMsg({ from: 'novah', text, ...extra });
    }, 400);
  }, [addMsg]);

  const user = useCallback((text: string) => {
    addMsg({ from: 'user', text });
  }, [addMsg]);

  // Reset
  const reset = () => {
    setMessages([]);
    setStep('source-choice');
    setStepHistory([]);
    setTyping(false);
    setUrl('');
    setScanResult(null);
    setBrandName('');
    setSelectedLogo(null);
    setSelectedPhotos(new Set());
    setSelectedTemplates(new Set());
    setGlobalTemplates([]);
    setContentMix({ education: 40, connection: 30, proof: 20, promotion: 10 });
    setIsCreating(false);
    setManualName('');
    setLogoFile(null);
    setLogoPreview('');
    setLogoUrlInput('');
    setSelectedFont(null);
    setUploadedPhotos([]);
    setPhotoUrlInput('');
    setCreatedWs(null);
    msgIdRef.current = 0;
    if (analyzeTimeoutRef.current) { clearTimeout(analyzeTimeoutRef.current); analyzeTimeoutRef.current = null; }
  };

  // Init greeting — explain what we're doing and offer options
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        addMsg({ from: 'novah', text: "Hi! I'm Novah, your brand assistant. First, I need to learn about your brand — your voice, style, colors, and what you do." });
      }, 300);
      setTimeout(() => {
        addMsg({ from: 'novah', text: "How would you like to share your brand info?" });
        setStep('source-choice');
      }, 1000);
    }
  }, [isOpen, messages.length, addMsg]);

  // Load detected fonts when entering font-preview step
  useEffect(() => {
    if (step === 'font-preview') {
      const fonts = scanResultRef.current?.brandFonts || scanResult?.brandFonts || [];
      fonts.forEach(font => {
        import('../utils/fontLoader').then(m => m.ensureFontLoaded(font));
      });
    }
  }, [step, scanResult]);

  const handleClose = () => { onClose(); reset(); };

  // ─── Scan ───

  const handleScan = async () => {
    if (!url.trim()) return;
    user(url.trim());
    setStep('scanning');

    addMsg({ from: 'novah', text: 'Let me take a look at your website...' });

    // Message queue for natural chat cadence
    const messageQueue: Array<{ text: string; action?: () => void }> = [];
    let queueRunning = false;
    const processQueue = () => {
      if (queueRunning || messageQueue.length === 0) return;
      queueRunning = true;
      const item = messageQueue.shift()!;
      setTimeout(() => {
        addMsg({ from: 'novah', text: item.text });
        item.action?.();
        queueRunning = false;
        processQueue();
      }, 600);
    };
    const enqueue = (text: string, action?: () => void) => {
      messageQueue.push({ text, action });
      processQueue();
    };

    // Track shown phases to deduplicate
    const shownPhases = new Set<string>();

    try {
      await apiClient.stream('/api/workspaces/scan-url', { url: url.trim() }, (event, data) => {
        const d = data as Record<string, unknown>;

        if (event === 'progress') {
          const s = d.step as string;

          if (s === 'extracting' && d.fetchStrategy && !shownPhases.has('extracted')) {
            shownPhases.add('extracted');
            const strategy = d.fetchStrategy as string;
            const strategyLabel = strategy === 'direct' ? 'Direct fetch' : strategy === 'googlebot' ? 'Googlebot' : 'Google Cache';
            const extra: string[] = [];
            if (d.usedFrameworkData) extra.push('Next.js data');
            if (d.usedJina) extra.push('Jina Reader');
            enqueue(`Website fetched via ${strategyLabel}${extra.length ? ' + ' + extra.join(', ') : ''} ✓`);

            const name = d.suggestedName as string;
            const logos = d.logoCount as number;
            const photos = d.photoCount as number;
            const colors = d.colorCount as number;
            const parts: string[] = [];
            if (name) parts.push(`Brand detected: **${name}**`);
            if (logos) parts.push(`${logos} logo${logos > 1 ? 's' : ''}`);
            if (photos) parts.push(`${photos} photo${photos > 1 ? 's' : ''}`);
            if (colors) parts.push(`${colors} brand color${colors > 1 ? 's' : ''}`);
            if (parts.length > 0) {
              enqueue(parts.join(' · '));
            }

            // Store images early so user can pick logo/photos while AI analyzes
            if (d.images && (d.images as ClassifiedImage[]).length > 0) {
              const earlyResult: ScanResult = {
                url: d.url as string || url.trim(),
                suggestedName: name || '',
                images: d.images as ClassifiedImage[],
                brandColors: (d.brandColors as string[]) || [],
                brandFonts: (d.brandFonts as string[]) || [],
                brandInfo: {},
              };
              setScanResult(earlyResult);
              scanResultRef.current = earlyResult;
              if (name) setBrandName(name);
            }
          }

          // When AI starts analyzing, skip to images immediately
          // Brand name is already set from extraction — go straight to logo selection
          if (s === 'analyzing' && !shownPhases.has('analyzing')) {
            shownPhases.add('analyzing');
            const hasImages = (scanResultRef.current?.images || []).length > 0;
            if (hasImages) {
              const imgCount = scanResultRef.current?.images.length || 0;
              enqueue(`I found ${imgCount} images. Which one is your logo? Pick one or skip.`, () => {
                // Force re-sync state from ref before showing logo-select
                // This ensures the UI renders with the images that are in the ref
                setScanResult(prev => prev || scanResultRef.current || prev);
                requestAnimationFrame(() => setStep('logo-select'));
              });
            } else {
              // No images found — skip logo/photo selection, confirm brand name
              enqueue("No images found. Let's confirm your brand name.", () => {
                pushStep('brand-name');
              });
            }

            if (analyzeTimeoutRef.current) clearTimeout(analyzeTimeoutRef.current);
            analyzeTimeoutRef.current = setTimeout(() => {
              // AI took too long — we already have images, just continue
              if (analyzeTimeoutRef.current) { analyzeTimeoutRef.current = null; }
            }, 90000);
          }
        }

        // AI analysis complete — silently store brand info (user is already picking images)
        if (event === 'complete') {
          if (analyzeTimeoutRef.current) { clearTimeout(analyzeTimeoutRef.current); analyzeTimeoutRef.current = null; }
          const completeResult: ScanResult = {
            url: d.url as string || scanResultRef.current?.url || '',
            suggestedName: d.suggestedName as string || scanResultRef.current?.suggestedName || '',
            images: (d.images as ClassifiedImage[]) || scanResultRef.current?.images || [],
            brandColors: (d.brandColors as string[]) || scanResultRef.current?.brandColors || [],
            brandFonts: (d.brandFonts as string[]) || scanResultRef.current?.brandFonts || [],
            brandInfo: d.brandInfo as Record<string, unknown> || scanResultRef.current?.brandInfo || {},
          };
          setScanResult(completeResult);
          scanResultRef.current = completeResult;
          if (d.suggestedName && !brandName) setBrandName(d.suggestedName as string);
        }

        if (event === 'error') {
          if (analyzeTimeoutRef.current) { clearTimeout(analyzeTimeoutRef.current); analyzeTimeoutRef.current = null; }
          // Clear queue and show error immediately
          messageQueue.length = 0;
          addMsg({ from: 'novah', text: `Oops: ${d.message || 'Scan failed'}. Try again or create manually.` });
          setStep('greeting');
        }
      });
    } catch (err) {
      messageQueue.length = 0;
      addMsg({ from: 'novah', text: `Failed to scan: ${err instanceof Error ? err.message : 'Unknown error'}. Try again or create manually.` });
      setStep('greeting');
    }
  };

  // ─── Step handlers ───

  const confirmBrandName = () => {
    const name = brandName.trim() || manualName.trim();
    if (!name) return;
    user(`Brand name: ${name}`);
    const fonts = scanResultRef.current?.brandFonts || scanResult?.brandFonts || [];
    if (fonts.length > 0) {
      novah("I found these fonts on your website. Pick one for your templates, or skip to use defaults.");
      pushStep('font-preview');
    } else {
      goToTemplates();
    }
  };

  const confirmFont = () => {
    if (selectedFont) {
      user(`Font: ${selectedFont}`);
    } else {
      user('Using default fonts');
    }
    goToTemplates();
  };

  const confirmLogo = () => {
    user(selectedLogo ? 'Logo selected ✓' : 'Skipped logo');
    const scanPhotos = allImages.filter(i => i.url !== selectedLogo);
    if (scanPhotos.length > 0) {
      novah(`I found ${scanPhotos.length} images on your site. Select the ones you want, upload your own, or skip.`);
    } else {
      novah("Want to add background images for your posts? Upload files, paste URLs, or skip.");
    }
    pushStep('photo-choice');
  };

  const goToBrandName = () => {
    novah(`I detected your brand name as **${brandName || 'Unknown'}**. Is this correct? Edit below if needed.`);
    pushStep('brand-name');
  };

  const confirmPhotos = () => {
    const totalPhotos = selectedPhotos.size + uploadedPhotos.length;
    user(totalPhotos > 0 ? `${totalPhotos} background image${totalPhotos > 1 ? 's' : ''} selected` : 'No backgrounds selected');
    goToBrandReview();
  };

  const goToBrandReview = () => {
    // Pre-fill review fields from scan result
    const info = scanResultRef.current?.brandInfo || scanResult?.brandInfo || {};
    setReviewDescription((info.description as string) || (info.businessOverview as string) || '');
    setReviewIndustry((info.productsServices as string[])?.join(', ') || '');
    setReviewPhone((info.phoneNumber as string) || '');
    setReviewEmail((info.email as string) || '');
    setReviewWebsite(scanResultRef.current?.url || scanResult?.url || '');
    const scannedColors = scanResultRef.current?.brandColors || scanResult?.brandColors || [];
    setReviewColors(scannedColors.slice(0, 6));
    novah("Here's what I found about your brand. Review and edit anything — especially your brand colors. I'll use them to style your ads!");
    pushStep('brand-review');
  };

  const confirmBrandReview = () => {
    // Save edited fields back to scanResult.brandInfo
    const info = { ...(scanResultRef.current?.brandInfo || scanResult?.brandInfo || {}) };
    if (reviewDescription) info.description = reviewDescription;
    if (reviewIndustry) info.productsServices = reviewIndustry.split(',').map(s => s.trim()).filter(Boolean);
    if (reviewPhone) info.phoneNumber = reviewPhone;
    if (reviewEmail) info.email = reviewEmail;
    if (reviewWebsite) info.sourceUrl = reviewWebsite;

    // Update scan result ref with colors
    if (scanResultRef.current) {
      scanResultRef.current = { ...scanResultRef.current, brandInfo: info, brandColors: reviewColors };
      setScanResult(scanResultRef.current);
    }

    user(`Brand info confirmed ✓${reviewColors.length ? ` · ${reviewColors.length} color${reviewColors.length > 1 ? 's' : ''}` : ''}`);
    goToBrandName();
  };

  const addPhotoUrl = () => {
    const trimmed = photoUrlInput.trim();
    if (!trimmed) return;
    setSelectedPhotos(prev => new Set([...prev, trimmed]));
    setPhotoUrlInput('');
    user(`Added: ${trimmed}`);
    novah('Got it! Add more or continue.');
  };

  const goToTemplates = () => {
    // Templates and content strategy are now selected at generation time, not during onboarding
    // Skip straight to workspace creation
    handleCreate();
  };

  const confirmTemplates = () => {
    // Legacy — no longer used in onboarding
    handleCreate();
  };


  const handleCreate = async () => {
    // Content mix defaults to balanced 25/25/25/25 — user can override at generation time
    setStep('creating');
    setIsCreating(true);

    setTimeout(() => addMsg({ from: 'novah', text: 'Creating your workspace...' }), 200);

    try {
      let workspace: Workspace;
      // Determine if logo is a real URL or a blob (file upload pending)
      const logoIsFile = logoFile && selectedLogo?.startsWith('blob:');
      const logoUrlForServer = logoIsFile ? undefined : (selectedLogo || undefined);

      if (scanResult) {
        workspace = await apiClient.post<Workspace>('/api/workspaces/create-from-scan', {
          name: brandName.trim(),
          url: scanResult.url,
          logoUrl: logoUrlForServer,
          backgroundImageUrls: [...selectedPhotos],
          brandInfo: scanResult.brandInfo,
          contentMix,
          selectedTemplateIds: [...selectedTemplates],
          brandColors: scanResult.brandColors || [],
          brandFonts: selectedFont ? [selectedFont] : (scanResult.brandFonts || []),
        });
      } else {
        workspace = await apiClient.post<Workspace>('/api/workspaces', { name: manualName.trim() });
      }

      // Upload logo file if provided (from logo-alt or manual flow)
      if (logoFile) {
        try {
          const fd = new FormData();
          fd.append('file', logoFile);
          fd.append('isLogo', 'true');
          await apiClient.upload<BrandAsset>(`/api/workspaces/${workspace.id}/assets/upload`, fd);
        } catch { /* logo upload failed, non-fatal */ }
      }

      // Global templates are referenced via selectedTemplateIds in the workspace record.
      // No need to clone — they show up in the template list automatically.
      // Users can clone manually from the templates page if they want a workspace copy.

      setCreatedWs(workspace);
      setTimeout(() => {
        const fontMsg = selectedFont ? ` Using **${selectedFont}** as your brand font.` : '';
        addMsg({ from: 'novah', text: `Your workspace **${workspace.name}** is ready!${fontMsg} Let's go.`, widget: 'success' });
        setStep('done');
        setIsCreating(false);
      }, 500);
    } catch (err) {
      addMsg({ from: 'novah', text: `Failed to create workspace: ${err instanceof Error ? err.message : 'Unknown error'}` });
      setIsCreating(false);
      setStep('content-mix');
    }
  };

  // ─── Render ───

  // Use ref as primary source (populated synchronously during SSE), fall back to state
  const allImages = scanResultRef.current?.images || scanResult?.images || [];

  const chatContent = (
    <>
      <ChatContainer>
        {messages.map(msg => (
          <MsgRow key={msg.id} $from={msg.from}>
            {msg.from === 'novah' && (
              <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
            )}
            <Bubble $from={msg.from} dangerouslySetInnerHTML={{ __html: msg.text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
          </MsgRow>
        ))}
        {typing && (
          <MsgRow $from="novah">
            <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
            <Bubble $from="novah"><TypingDots><span /><span /><span /></TypingDots></Bubble>
          </MsgRow>
        )}
        <div ref={scrollRef} />
      </ChatContainer>

      {/* ─── Interactive area ─── */}
      <InputArea>
        {/* Source choice: how to share brand info */}
        {step === 'source-choice' && (
          <SourceChoiceGrid>
            <SourceChoiceBtn onClick={() => {
              user('I have a website');
              novah("Paste your website URL and I'll extract everything automatically.");
              pushStep('url-input');
            }}>
              <SourceIcon>🌐</SourceIcon>
              <div><SourceLabel>Scan my website</SourceLabel><br /><SourceDesc>I'll extract your brand info, logo, and images automatically</SourceDesc></div>
            </SourceChoiceBtn>
            <SourceChoiceBtn onClick={() => {
              user("I'll enter it manually");
              novah("No problem! What's your brand name?");
              pushStep('manual-name');
            }}>
              <SourceIcon>✏️</SourceIcon>
              <div><SourceLabel>Enter manually</SourceLabel><br /><SourceDesc>Type your brand name and upload a logo</SourceDesc></div>
            </SourceChoiceBtn>
            <SourceChoiceBtn onClick={() => {
              user('I have a document');
              novah("Great! Upload a PDF, DOCX, or TXT file with your brand info and I'll analyze it.");
              pushStep('document-upload');
            }}>
              <SourceIcon>📄</SourceIcon>
              <div><SourceLabel>Upload a document</SourceLabel><br /><SourceDesc>PDF, brand guide, or any file with your brand info</SourceDesc></div>
            </SourceChoiceBtn>
          </SourceChoiceGrid>
        )}

        {/* Greeting: just typing animation, no input — transitions to source-choice */}
        {step === 'greeting' && (
          <div style={{ textAlign: 'center', padding: 8 }}>
            <TypingDots><span /><span /><span /></TypingDots>
          </div>
        )}

        {/* URL input — shown when user picks "Scan my website" from source-choice */}
        {step === 'url-input' && (
          <>
            <UrlRow>
              <UrlInput
                value={url}
                onChange={e => setUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleScan(); } }}
                placeholder="https://yourbrand.com"
                autoFocus
              />
              <Button type="button" onClick={handleScan} disabled={!url.trim()} $size="sm">Scan</Button>
            </UrlRow>
          </>
        )}

        {/* Scanning: just show typing/messages, no input */}
        {step === 'scanning' && (
          <div style={{ textAlign: 'center', fontSize: 12, color: '#999', padding: 8 }}>
            Scanning in progress...
          </div>
        )}

        {/* Brand review — show what we found, let user edit */}
        {step === 'brand-review' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ background: '#f8f9fb', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</label>
              <textarea
                value={reviewDescription}
                onChange={e => setReviewDescription(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="What does this brand do?"
              />

              <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Products / Services</label>
              <input
                value={reviewIndustry}
                onChange={e => setReviewIndustry(e.target.value)}
                style={{ padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }}
                placeholder="e.g. Web design, Marketing, Coffee"
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</label>
                  <input
                    value={reviewPhone}
                    onChange={e => setReviewPhone(e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, marginTop: 4 }}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</label>
                  <input
                    value={reviewEmail}
                    onChange={e => setReviewEmail(e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, marginTop: 4 }}
                    placeholder="contact@brand.com"
                  />
                </div>
              </div>

              <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Website</label>
              <input
                value={reviewWebsite}
                onChange={e => setReviewWebsite(e.target.value)}
                style={{ padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }}
                placeholder="https://yourbrand.com"
              />

              {/* Brand colors — interactive picker */}
              <div>
                <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Brand Colors {reviewColors.length > 0 && <span style={{ color: '#3B82F6' }}>({reviewColors.length})</span>}
                </label>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {reviewColors.map((c, i) => (
                    <div
                      key={i}
                      style={{ width: 32, height: 32, borderRadius: 8, background: c, border: '2px solid #e2e8f0', cursor: 'pointer', position: 'relative' }}
                      title={`${c} — click to remove`}
                      onClick={() => setReviewColors(prev => prev.filter((_, idx) => idx !== i))}
                    >
                      <span style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, opacity: 0.8 }}>×</span>
                    </div>
                  ))}
                  {reviewColors.length < 6 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="color"
                        value={colorInput}
                        onChange={e => setColorInput(e.target.value)}
                        style={{ width: 32, height: 32, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 0 }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (colorInput && !reviewColors.includes(colorInput)) {
                            setReviewColors(prev => [...prev, colorInput]);
                          }
                        }}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8f9fb', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                      >
                        + Add
                      </button>
                    </div>
                  )}
                </div>
                {reviewColors.length === 0 && (
                  <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>No colors detected. Add your brand colors above — I&apos;ll use them in your ads!</p>
                )}
              </div>
            </div>

            <Button type="button" onClick={confirmBrandReview} style={{ width: '100%' }}>
              Looks good, continue
            </Button>
          </div>
        )}

        {/* Brand name confirmation */}
        {step === 'brand-name' && (
          <>
            <InputWrapper style={{ marginBottom: 8 }}>
              <Input
                value={brandName}
                onChange={e => setBrandName(e.target.value)}
                placeholder="Brand name"
                autoFocus
              />
            </InputWrapper>
            <Button type="button" onClick={confirmBrandName} disabled={!brandName.trim()} style={{ width: '100%' }}>
              Confirm Name
            </Button>
          </>
        )}

        {/* Font preview */}
        {step === 'font-preview' && (
          <>
            <FontPreviewList>
              {(scanResultRef.current?.brandFonts || scanResult?.brandFonts || []).map(font => (
                <FontPreviewItem
                  key={font}
                  $selected={selectedFont === font}
                  onClick={() => setSelectedFont(prev => prev === font ? null : font)}
                >
                  <FontName>{font}</FontName>
                  <FontSample style={{ fontFamily: font }}>
                    The quick brown fox jumps over the lazy dog
                  </FontSample>
                </FontPreviewItem>
              ))}
            </FontPreviewList>
            <Button type="button" onClick={confirmFont} style={{ width: '100%' }}>
              {selectedFont ? `Use ${selectedFont}` : 'Skip — use defaults'}
            </Button>
          </>
        )}

        {/* Logo selection */}
        {step === 'logo-select' && (() => {
          const logos = allImages.filter(i => i.category === 'logo');
          const photos = allImages.filter(i => i.category !== 'logo');
          const sortedImages = [...logos, ...photos];
          return (
          <>
            {logos.length > 0 && (
              <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Possible logos ({logos.length})
              </div>
            )}
            <ImgGrid>
              {sortedImages.map((img, i) => (
                <ImgItem
                  key={i}
                  $selected={selectedLogo === img.url}
                  onClick={() => setSelectedLogo(selectedLogo === img.url ? null : img.url)}
                >
                  <img src={img.url} alt={`Image ${i + 1}`} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  {selectedLogo === img.url && <Badge>✓</Badge>}
                  {img.category === 'logo' && selectedLogo !== img.url && (
                    <div style={{ position: 'absolute', top: 2, right: 2, fontSize: 8, background: 'rgba(59,130,246,0.8)', color: '#fff', padding: '1px 4px', borderRadius: 4, fontWeight: 700 }}>LOGO</div>
                  )}
                </ImgItem>
              ))}
            </ImgGrid>
            <ModalActions>
              <Button type="button" $variant="ghost" $size="sm" onClick={() => {
                user("None of these is my logo");
                setSelectedLogo(null);
                novah("No worries! You can paste a logo URL or upload a file.");
                pushStep('logo-alt');
              }}>
                None of these
              </Button>
              <Button type="button" $variant="outline" $size="sm" onClick={() => { setSelectedLogo(null); confirmLogo(); }}>
                Skip
              </Button>
              <Button type="button" $size="sm" onClick={confirmLogo} disabled={!selectedLogo}>
                Confirm Logo
              </Button>
            </ModalActions>
          </>
          );
        })()}

        {/* Logo alternative: paste URL or upload */}
        {step === 'logo-alt' && (
          <>
            <InputWrapper style={{ marginBottom: 8 }}>
              <UrlInput
                value={logoUrlInput}
                onChange={e => { setLogoUrlInput(e.target.value); setSelectedLogo(e.target.value); }}
                placeholder="Paste logo URL (https://...)"
              />
            </InputWrapper>
            <div style={{ textAlign: 'center', fontSize: 11, color: '#999', margin: '4px 0 8px' }}>or</div>
            <HiddenInput ref={fileRef} type="file" accept="image/*" onChange={e => {
              const f = e.target.files?.[0];
              if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); setSelectedLogo(URL.createObjectURL(f)); }
            }} />
            <Button type="button" $variant="outline" $size="sm" onClick={() => fileRef.current?.click()} style={{ width: '100%', marginBottom: 8 }}>
              {logoFile ? logoFile.name : 'Upload Logo File'}
            </Button>
            {logoPreview && (
              <div style={{ width: 64, height: 64, margin: '0 auto 8px', border: '2px dashed #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 8 }}>
                <img src={logoPreview} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
            )}
            <ModalActions>
              <Button type="button" $variant="outline" $size="sm" onClick={() => {
                setSelectedLogo(null); setLogoFile(null); setLogoPreview(''); setLogoUrlInput('');
                user('Skipped logo');
                confirmLogo();
              }}>
                Skip
              </Button>
              <Button type="button" $size="sm" onClick={() => {
                // Use URL input or file
                const finalUrl = logoUrlInput.trim() || (logoFile ? logoPreview : null);
                setSelectedLogo(finalUrl);
                user(logoFile ? 'Logo uploaded ✓' : finalUrl ? 'Logo URL added ✓' : 'Skipped logo');
                confirmLogo();
              }} disabled={!logoUrlInput.trim() && !logoFile}>
                Use This Logo
              </Button>
            </ModalActions>
          </>
        )}

        {/* Unified photo selection — scan images + upload + URL, all in one */}
        {(step === 'photo-choice' || step === 'photo-select' || step === 'photo-upload') && (() => {
          const scanPhotos = allImages.filter(i => i.url !== selectedLogo);
          return (
            <>
              <HiddenInput
                ref={photoFileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) {
                    setUploadedPhotos(prev => [...prev, ...files]);
                    files.forEach(f => {
                      const blobUrl = URL.createObjectURL(f);
                      setSelectedPhotos(prev => new Set([...prev, blobUrl]));
                    });
                  }
                  e.target.value = '';
                }}
              />

              {/* Scan images */}
              {scanPhotos.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                    From your website ({scanPhotos.length})
                  </div>
                  <ImgGrid>
                    {scanPhotos.map((img, i) => (
                      <ImgItem
                        key={`scan-${i}`}
                        $selected={selectedPhotos.has(img.url)}
                        onClick={() => {
                          setSelectedPhotos(prev => {
                            const next = new Set(prev);
                            next.has(img.url) ? next.delete(img.url) : next.add(img.url);
                            return next;
                          });
                        }}
                      >
                        <img src={img.url} alt={`Scan ${i + 1}`} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        {selectedPhotos.has(img.url) && <Badge>✓</Badge>}
                      </ImgItem>
                    ))}
                  </ImgGrid>
                </>
              )}

              {/* Uploaded / pasted images */}
              {[...selectedPhotos].filter(u => !scanPhotos.some(s => s.url === u)).length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 8, marginBottom: 4 }}>
                    Your uploads ({[...selectedPhotos].filter(u => !scanPhotos.some(s => s.url === u)).length})
                  </div>
                  <ImgGrid>
                    {[...selectedPhotos].filter(u => !scanPhotos.some(s => s.url === u)).map((url, i) => (
                      <ImgItem key={`upload-${i}`} $selected>
                        <img src={url} alt={`Upload ${i + 1}`} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        <Badge>✓</Badge>
                      </ImgItem>
                    ))}
                  </ImgGrid>
                </>
              )}

              {/* Add more: URL input + upload button */}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, marginBottom: 8 }}>
                <Input
                  value={photoUrlInput}
                  onChange={e => setPhotoUrlInput(e.target.value)}
                  placeholder="Paste image URL..."
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPhotoUrl(); } }}
                  style={{ flex: 1 }}
                />
                <Button type="button" $size="sm" onClick={addPhotoUrl} disabled={!photoUrlInput.trim()}>Add</Button>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button type="button" $variant="outline" $size="sm" onClick={() => photoFileRef.current?.click()} style={{ flex: 1 }}>
                  Upload Files
                </Button>
                <Button type="button" $size="sm" onClick={confirmPhotos} style={{ flex: 1 }}>
                  {selectedPhotos.size > 0 ? `Continue with ${selectedPhotos.size} image${selectedPhotos.size > 1 ? 's' : ''}` : 'Skip'}
                </Button>
              </div>
            </>
          );
        })()}

        {/* Template selection */}
        {step === 'template-select' && (
          <>
            <TplActions>
              <span style={{ color: '#64748B' }}>{selectedTemplates.size}/{globalTemplates.length} selected</span>
              <SelectAllLink onClick={() => {
                if (selectedTemplates.size === globalTemplates.length) {
                  setSelectedTemplates(new Set());
                } else {
                  setSelectedTemplates(new Set(globalTemplates.map(t => t.id)));
                }
              }}>
                {selectedTemplates.size === globalTemplates.length ? 'Deselect all' : 'Select all'}
              </SelectAllLink>
            </TplActions>
            <TplGrid>
              {globalTemplates.map(tpl => (
                <TemplateThumbnail
                  key={tpl.id}
                  templateId={tpl.id}
                  selected={selectedTemplates.has(tpl.id)}
                  onClick={() => {
                    setSelectedTemplates(prev => {
                      const next = new Set(prev);
                      next.has(tpl.id) ? next.delete(tpl.id) : next.add(tpl.id);
                      return next;
                    });
                  }}
                />
              ))}
            </TplGrid>
            <Button type="button" onClick={confirmTemplates} disabled={selectedTemplates.size === 0} style={{ width: '100%' }} $size="sm">
              {selectedTemplates.size === 0 ? 'Select at least 1 template' : `Continue with ${selectedTemplates.size} template${selectedTemplates.size !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}

        {/* Content mix */}
        {step === 'content-mix' && (
          <>
            <MixCheckboxGrid>
              {([
                { key: 'education' as const, label: 'Education', hint: 'Tips, how-tos' },
                { key: 'connection' as const, label: 'Connection', hint: 'Community, story' },
                { key: 'proof' as const, label: 'Proof', hint: 'Testimonials, results' },
                { key: 'promotion' as const, label: 'Promotion', hint: 'Sales, CTAs' },
              ]).map(({ key, label, hint }) => (
                <MixCheckItem key={key} $checked={checkedCategories.has(key)}>
                  <MixCheckbox
                    type="checkbox"
                    checked={checkedCategories.has(key)}
                    onChange={() => toggleCategory(key)}
                  />
                  <div>
                    <MixLabel>{label}</MixLabel>
                    <MixHint> — {hint}</MixHint>
                  </div>
                </MixCheckItem>
              ))}
            </MixCheckboxGrid>
            <MixSummary>
              {Array.from(checkedCategories).map(c => `${c.charAt(0).toUpperCase() + c.slice(1)} ${contentMix[c]}%`).join(' · ')}
            </MixSummary>
            <Button type="button" onClick={handleCreate} disabled={isCreating || checkedCategories.size === 0}
              style={{ width: '100%', marginTop: 8 }}>
              {isCreating ? 'Creating...' : 'Create Workspace'}
            </Button>
          </>
        )}

        {/* Creating */}
        {step === 'creating' && (
          <div style={{ textAlign: 'center', fontSize: 12, color: '#999', padding: 8 }}>
            Setting up your workspace...
          </div>
        )}

        {/* Done */}
        {step === 'done' && createdWs && (
          <SuccessBox>
            <Button type="button" onClick={() => { onCreated(createdWs); handleClose(); }} style={{ width: '100%' }}>
              Go to Workspace
            </Button>
          </SuccessBox>
        )}

        {/* Manual: name input */}
        {step === 'manual-name' && (
          <>
            <InputWrapper style={{ marginBottom: 8 }}>
              <Input
                value={manualName}
                onChange={e => setManualName(e.target.value)}
                placeholder="e.g., Acme Corp"
                autoFocus
              />
            </InputWrapper>
            <Button type="button" onClick={() => {
              if (!manualName.trim()) return;
              user(`Brand name: ${manualName.trim()}`);
              setBrandName(manualName.trim());
              novah("Got it! Now, do you have a logo? You can upload a file, paste a URL, or skip for now.");
              pushStep('manual-logo');
            }} disabled={!manualName.trim()} style={{ width: '100%' }}>
              Continue
            </Button>
          </>
        )}

        {/* Manual: logo — upload file, paste URL, or skip */}
        {step === 'manual-logo' && (
          <>
            <HiddenInput ref={fileRef} type="file" accept="image/*" onChange={e => {
              const f = e.target.files?.[0];
              if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); setLogoUrlInput(''); }
            }} />

            {/* Preview */}
            {(logoPreview || logoUrlInput.trim()) && (
              <div style={{ width: 72, height: 72, margin: '0 auto 10px', border: '2px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 12, background: '#f8fafc' }}>
                <img src={logoPreview || logoUrlInput.trim()} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
            )}

            {/* Options as source choice cards */}
            <SourceChoiceGrid>
              <SourceChoiceBtn onClick={() => fileRef.current?.click()}>
                <SourceIcon>📁</SourceIcon>
                <div>
                  <SourceLabel>{logoFile ? logoFile.name : 'Upload from device'}</SourceLabel>
                  <br /><SourceDesc>JPG, PNG, WebP, SVG</SourceDesc>
                </div>
              </SourceChoiceBtn>
            </SourceChoiceGrid>

            {/* URL input */}
            <div style={{ margin: '8px 0' }}>
              <UrlRow>
                <UrlInput
                  value={logoUrlInput}
                  onChange={e => { setLogoUrlInput(e.target.value); setSelectedLogo(e.target.value); setLogoFile(null); setLogoPreview(''); }}
                  placeholder="Or paste a logo URL..."
                />
              </UrlRow>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="button" $variant="ghost" $size="sm" onClick={() => {
                user('Skipped logo');
                goToTemplates();
              }} style={{ flex: 1 }}>Skip for now</Button>
              <Button type="button" $size="sm" onClick={() => {
                const hasLogo = logoFile || logoUrlInput.trim() || logoPreview;
                if (hasLogo) {
                  if (logoUrlInput.trim()) setSelectedLogo(logoUrlInput.trim());
                  user('Logo added ✓');
                } else {
                  user('Skipped logo');
                }
                goToTemplates();
              }} style={{ flex: 1 }} disabled={false}>
                {(logoFile || logoUrlInput.trim()) ? 'Continue' : 'Skip'}
              </Button>
            </div>
          </>
        )}
        {/* Document upload step */}
        {step === 'document-upload' && (
          <>
            <HiddenInput ref={docFileRef} type="file" accept=".pdf,.docx,.txt,.doc" onChange={e => {
              const f = e.target.files?.[0];
              if (f) setDocFile(f);
            }} />

            {docFile && (
              <div style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, marginBottom: 8, fontSize: 13, color: '#166534' }}>
                {docFile.name} ({(docFile.size / 1024).toFixed(0)} KB)
              </div>
            )}

            <SourceChoiceGrid>
              <SourceChoiceBtn onClick={() => docFileRef.current?.click()}>
                <SourceIcon>📄</SourceIcon>
                <div>
                  <SourceLabel>{docFile ? 'Change file' : 'Choose file'}</SourceLabel>
                  <br /><SourceDesc>PDF, DOCX, or TXT</SourceDesc>
                </div>
              </SourceChoiceBtn>
            </SourceChoiceGrid>

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Button type="button" $variant="ghost" $size="sm" onClick={() => {
                goBack();
                setDocFile(null);
              }} style={{ flex: 1 }}>Back</Button>
              <Button type="button" $size="sm" disabled={!docFile} onClick={async () => {
                if (!docFile) return;
                user(`Uploaded: ${docFile.name}`);
                novah("Analyzing your document...");
                pushStep('document-analyzing');

                try {
                  const formData = new FormData();
                  formData.append('file', docFile);

                  const result = await apiClient.postFormData<{ brandInfo: Record<string, unknown> }>('/api/workspaces/analyze-document', formData);

                  const info = result.brandInfo || {};
                  // Store in scanResult so createFromScan can use it
                  setScanResult({
                    url: '',
                    suggestedName: String(info.description || '').split('.')[0]?.substring(0, 50) || docFile.name.replace(/\.[^.]+$/, ''),
                    images: [],
                    brandColors: [],
                    brandFonts: [],
                    brandInfo: info,
                  });

                  const desc = String(info.description || 'your business').substring(0, 120);
                  novah(`I analyzed your document! It seems to be about: ${desc}. What's the brand name?`);
                  setStep('manual-name');
                } catch (err) {
                  console.error('Document analysis failed:', err);
                  novah("Sorry, I couldn't analyze that document. You can try a different file or enter your brand info manually.");
                  setStep('source-choice');
                }
              }} style={{ flex: 1 }}>Analyze</Button>
            </div>
          </>
        )}

        {step === 'document-analyzing' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <TypingDots><span /><span /><span /></TypingDots>
            <p style={{ color: '#64748B', fontSize: 13, marginTop: 8 }}>Reading your document...</p>
          </div>
        )}

        {stepHistory.length > 0 && !['greeting', 'scanning', 'creating', 'document-analyzing', 'done'].includes(step) && (
          <BackBtn onClick={goBack}>{'\u2190'} Go back</BackBtn>
        )}
      </InputArea>
    </>
  );

  if (inline) {
    if (!isOpen) return null;
    return chatContent;
  }

  const isBusy = step === 'scanning' || step === 'creating';
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="540px"
      closeOnOverlayClick={!isBusy}
      closeOnEscape={!isBusy}
    >
      {chatContent}
    </Modal>
  );
}
